import React, { ReactNode } from 'react';
import { SHA256 } from 'crypto-js';
import { Scan } from 'app/dashboard/interfaces/scan.interfaces';
import {
  GetLatestQuestionnaireByIdQuery,
  GetQuestionnaireResponsesByUserAndQuestionnaireIdQuery,
  GetUnauthenticatedQuestionsByIdsQuery,
  Hasura_Test_Types_Enum,
  Image_Source_Types_Enum,
  Skintelligent_Tests,
  User_Notification_Status_Enum,
} from 'graphql/generated/hasura';
import { ContactData } from 'app/my-account/interfaces/profile.interfaces';
import Compressor from 'compressorjs';

import { FhirSkintelligentTest } from 'app/my-skin/interfaces/scan.interfaces';
import {
  ALL_ALLOWED_CHARACTERS,
  ALLOWED_ONLY_ALPHABETICAL_AND_NUMBERS_CHARACTERS_WITH_HYPEN,
  ALLOWED_ONLY_ALPHABETICAL_CHARACTERS_AND_SPACES,
  ALLOWED_ONLY_ALPHABETICAL_CHARACTERS_REGEX,
  ALLOWED_ONLY_ALPHABETICAL_CHARACTERS_WITH_HYPEN,
  ALLOWED_ONLY_NUMBERS,
  capitalizedLabels,
  FULL_PHONE_NUMBER_SEPARATED_BY_GROUPS_REGEX,
  MAX_ZIP_CODE_LENGTH,
  SYSTOLIC_AND_DIASTOLIC_QUESTION_ID,
} from './constants';
import { DropdownItem } from '../components/dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import {
  Appointment_Event,
  AppointmentEvent,
  AppointmentTypeEnum,
  GetFhirPatientMediaQuery,
  GetFhirQuestionnaireResponseQuery,
  HasuraAnswerItem,
  ServiceType,
} from 'graphql/generated/remote-schema-hasura';
import { MY_PATIENTS, MY_PATIENTS_BIO_INFO } from './routes';
import {
  MediaIdentifiersTypes,
  SharedMedia,
} from 'app/my-patients/interfaces/interfaces';
import { Roles } from '../firebase/interfaces';
import { MINUTES_IN_HOUR, SECONDS_IN_MIN } from './constants/time';
import { Notification } from '../app/dashboard/interfaces/notifications.interfaces';
import { AddressInformationOutput } from '../app/my-appointments/components/select-a-provider/ProviderCardProps';
import moment from 'moment-timezone';
import { Notification as ApiNotification } from '../hooks/useNotifications';
import { AlertExtraActions } from './interfaces';

export const calculateSHA256Hash = (content: string): string => {
  return SHA256(content).toString();
};

export function capitalizeFirstLetter(value?: string | null) {
  if (!value) {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export const sortScansByDate = (
  scans: Scan[],
  direction: string | undefined,
): Scan[] => {
  return [...scans].sort((a, b) => {
    if (!a.date || !b.date) {
      return 0;
    }
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      return 0;
    }
    return direction === 'ascending'
      ? dateA.getTime() - dateB.getTime()
      : dateB.getTime() - dateA.getTime();
  });
};

export const populateScanResult = (
  scan: Partial<Skintelligent_Tests>,
): Scan => {
  return {
    id: scan?.test_id,
    reportId: scan?.report_id,
    date: formatDateToCustomFormat(scan?.skintelligent_tests_test?.created_at),
    imageId: scan?.result_image_id || '',
    inflammations: Number(scan?.inflammatory),
    comedones: Number(scan?.comedone),
    pih: Number(scan?.pih),
    total: Number(scan?.total_lesions),
    source: scan?.skintelligent_tests_image_source_type
      ?.value as Image_Source_Types_Enum,
  };
};

export const mapFhirSkintelligentTest = (
  test: FhirSkintelligentTest,
): Skintelligent_Tests => {
  return {
    ...test,
    skintelligent_tests_image_source_type: {
      value: test.image_source,
    },
    skintelligent_tests_test: {
      created_at: test.created_at,
    },
  } as unknown as Skintelligent_Tests;
};

export const formatMMDDYYYYtoYYYYMMDD = (date: string) => {
  const parts = date.split('/');
  const month = parts[0];
  const day = parts[1];
  const year = parts[2];

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const formatCurrentDateToYYYYMMDD = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const formatDateToCustomFormat = (
  dateTimeString: string,
  onlyDate = false,
): string => {
  const dateObj = new Date(dateTimeString);
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours() % 12 || 12).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const timeZoneName = dateObj
    .toLocaleString(['en-US'], { timeZoneName: 'short' })
    .split(' ')
    .at(-1);
  const ampm = dateObj.getHours() >= 12 ? 'PM' : 'AM';

  const dateString = `${month}/${day}/${year}`;

  if (onlyDate) {
    return dateString;
  }
  return `${dateString} ${hours}:${minutes} ${ampm} ${timeZoneName}`;
};

export const getUserTimeZoneOffset = (): string => {
  const userTimezoneOffset = new Date().getTimezoneOffset();
  // Convert the timezone offset to hours and minutes
  const timezoneOffsetHours = Math.abs(Math.floor(userTimezoneOffset / 60))
    .toString()
    .padStart(2, '0');
  const timezoneOffsetMinutes = Math.abs(userTimezoneOffset % 60)
    .toString()
    .padStart(2, '0');
  const timezoneOffsetSign = userTimezoneOffset < 0 ? '+' : '-';
  return `${timezoneOffsetSign}${timezoneOffsetHours}:${timezoneOffsetMinutes}`;
};

export const getUserBrowserTimeZone = (
  date: Date | string = new Date(),
): string => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'GMT';
  const m = moment(date).tz(tz);

  return m.format('z') || `GMT${m.format('Z')}`;
};

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        console.error('Failed to convert image to Base64.');
        reject(new Error('Failed to convert image to Base64.'));
      }
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

export const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
};

export const base64ToBlob = (
  base64Data: string,
  contentType = 'application/pdf',
): Blob => {
  const binaryString = window.atob(base64Data);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < binaryString.length; offset += 512) {
    const slice = binaryString.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export function dataURLToFile(dataURL: string, fileName: string) {
  const [header, base64Data] = dataURL.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mimeType = mimeMatch ? mimeMatch[1] : '';

  const byteString = atob(base64Data);
  const byteArray = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  return new File([byteArray], fileName, { type: mimeType });
}

export const downloadPdf = (
  fileUrl: string,
  fileName: string,
  blank?: boolean,
) => {
  const anchor = document.createElement('a');
  anchor.href = fileUrl;
  anchor.download = fileName;
  if (blank) {
    anchor.target = '_blank';
  }
  anchor.click();
  window.URL.revokeObjectURL(fileUrl);
};

export const printPdf = (fileUrl: string) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  iframe.src = fileUrl;

  iframe.onload = () => {
    try {
      if (iframe.contentWindow) {
        iframe.focus();
        iframe.contentWindow.print();
      }
    } catch (error) {
      console.error('Cross-origin error — cannot print directly:', error);
      window.open(fileUrl, '_blank');
    }
  };
};

export const excludeItemsFromElementsArray = (
  arr: Element[],
  itemsToExclude: Element[],
) => {
  const remainingItems = arr.filter(
    (currentItem) => !itemsToExclude.includes(currentItem),
  );
  return remainingItems;
};

export const compressImage = (imageFile: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    new Compressor(imageFile, {
      quality: 0.6,
      success(result) {
        resolve(result);
      },
      error(error) {
        reject(error);
      },
    });
  });
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  const reader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export type GroupedNotification = {
  label: string;
  items: Notification[];
  onCloseModal?: () => void;
};

export const filterNoToastNotification = (notifications: Notification[]) => {
  return notifications.filter((notification) => !notification.is_toast);
};

export function groupNotificationsByFormattedDate(
  notifications: Notification[],
): GroupedNotification[] {
  const grouped = new Map<string, Notification[]>();

  for (const notification of notifications) {
    const group = grouped.get(notification.groupRelativeDate as string) ?? [];
    group.push(notification);
    grouped.set(notification.groupRelativeDate as string, group);
  }

  return Array.from(grouped.entries()).map(([label, items]) => ({
    label,
    items,
  }));
}

export const relativeDateForGroup = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const isYesterday = (d: Date) => {
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    return isSameDay(d, yesterday);
  };

  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (isSameDay(date, now)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  if (diffInDays > 7 && diffInDays < 30) return 'One week ago';

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const relativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const units = [
    { label: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
    { label: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
    { label: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
    { label: 'day', ms: 24 * 60 * 60 * 1000 },
    { label: 'hour', ms: 60 * 60 * 1000 },
    { label: 'minute', ms: 60 * 1000 },
  ];

  if (diff < units[units.length - 1].ms) return 'Just now';
  if (diff < units[2].ms && Math.floor(diff / units[3].ms) === 1)
    return 'Yesterday';

  for (const { label, ms } of units) {
    if (diff >= ms) {
      const value = Math.floor(diff / ms);
      return `${value} ${label}${value > 1 ? 's' : ''} ago`;
    }
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const interpolateVariables = (
  message: string,
  variables: { [key: string]: string },
): string => {
  if (message && message?.trim() === '') {
    return message;
  }

  const areAllVariablesEmpty = Object.values(variables).every(
    (value) => value?.trim() === '',
  );

  if (areAllVariablesEmpty) {
    return message;
  }

  Object.keys(variables).forEach((key) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    message = message && message.replace(regex, variables[key]);
  });
  return message;
};

export const interpolateReactNodes = (
  message: string,
  variables: { [key: string]: ReactNode },
): ReactNode => {
  if (!message || message.trim() === '') {
    return message;
  }

  const regex = /{{(.*?)}}/g;
  const result: ReactNode[] = [];

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(message)) !== null) {
    const [placeholder, key] = match;
    const variableNode = variables[key];

    if (match.index > lastIndex) {
      result.push(message.slice(lastIndex, match.index));
    }

    if (variableNode !== undefined) {
      result.push(variableNode);
    } else {
      result.push(placeholder);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < message.length) {
    result.push(message.slice(lastIndex));
  }

  return result.length > 1 ? <>{result}</> : result[0];
};

export type ObjectValidate = Record<string, string>;

export const areAllKeysDefined = (
  objToValidate: ObjectValidate,
  requiredKeys: string[] = [],
): boolean => {
  for (const key of requiredKeys) {
    const keys = key.split('.');
    let currentObj = objToValidate;

    for (const nestedKey of keys) {
      const value = currentObj?.[nestedKey];
      if (
        (!value && typeof value !== 'number') ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return false;
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        currentObj = value;
      }
    }
  }
  return true;
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
};

// The input date should be in mm/dd/yyyy format.
export const validateDateFormat = (date: string): boolean => {
  const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (dateFormatRegex.test(date)) {
    const [month, day, year] = date.split('/');
    const parsedDate = new Date(`${year}-${month}-${day}`);

    if (!isNaN(parsedDate.getTime())) {
      return true;
    }
  }
  return false;
};

export const addMaskToDate = (value: string): string => {
  value = value.replace(/\D/g, '');

  if (value.length >= 2 && value.length <= 4) {
    value = value.slice(0, 2) + '/' + value.slice(2, 4);
  } else if (value.length >= 4) {
    value =
      value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4, 8);
  }
  return value;
};

export const normalizePhoneNumber = (number: string) =>
  number.match(/\d+/g)?.join('') || '';

export const normalizePhoneNumberByUserRole = (number: string, role?: string) =>
  role === Roles.PROVIDER
    ? number.match(/\d+/g)?.slice(1)?.join('') || ''
    : number.match(/\d+/g)?.join('') || '';

interface CountriesValues {
  code: string | null | undefined;
  flag: string | null | undefined;
}

export interface CountriesSelectValue {
  label: string;
  value: string;
  flag: string;
}

interface FullContactData extends ContactData {
  extension: string;
}

export const parseMappedCountriesIntoSelectValues = (
  countriesValues: CountriesValues[],
) =>
  countriesValues.map((value) => ({
    label: value.code,
    value: value.code,
    flag: value.flag,
  }));

export const parseStringArrayIntoSelectOptions = (values: string[]) => {
  return values.map((value) => {
    return { label: value, value: value };
  });
};

export const parseSelectedValue = (
  currentCountry: string,
  mappedCountriesValues: CountriesValues[],
) => {
  const values = parseMappedCountriesIntoSelectValues(mappedCountriesValues);
  const matchingValue = values.find((item) => item.value === currentCountry);
  return matchingValue;
};

export const getCurrentCountryValue = (
  formValues: FullContactData,
  mappedCountriesValues: CountriesValues[],
) => parseSelectedValue(formValues.country, mappedCountriesValues);

export const getDefaultParsedValue = (
  mappedCountriesValues: CountriesValues[],
) => parseMappedCountriesIntoSelectValues(mappedCountriesValues)[0];

export const getParsedCountries = (mappedCountriesValues: CountriesValues[]) =>
  parseMappedCountriesIntoSelectValues(mappedCountriesValues);
export const isArrayOfStrings = (arr: unknown[]) => {
  for (let i = 0; i < arr.length; i++) {
    if (typeof arr[i] !== 'string') {
      return false;
    }
  }
  return true;
};

export const formatDateToAppointment = (date: string) => {
  const options = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  };

  const formattedDate = new Intl.DateTimeFormat(
    'en-US',
    options as never,
  ).format(new Date(date));
  return formattedDate;
};

export const avoidSpacesOnKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
) => {
  if (e.key === ' ') {
    e.preventDefault(); // Prevent entering the space
  }
};

export const allowOnlyNumbers = (value: string) => {
  const numericRegex = /^[0-9]*$/;
  return numericRegex.test(value) || value === '';
};

export const allowOnlyLettersWithAllowedCharacters = (value: string) => {
  const nameRegex = ALLOWED_ONLY_ALPHABETICAL_CHARACTERS_WITH_HYPEN;
  return nameRegex.test(value) || value === '';
};

export const allowAllCharactersWithAllowedCharacters = (value: string) => {
  const nameRegex = ALL_ALLOWED_CHARACTERS;
  return nameRegex.test(value) || value === '';
};

export const allowOnlyStringWithNoSpaces = (value: string) => {
  const regex = /^[^\s]*$/;
  return regex.test(value);
};

export const allowCharactersWithMaxLength = (
  value: string,
  maxLength = 100,
) => {
  const regex = ALLOWED_ONLY_ALPHABETICAL_AND_NUMBERS_CHARACTERS_WITH_HYPEN;
  return regex.test(value) && value.length <= maxLength;
};

export const allowOnlyNumbersAndMaxLengthForZipCodes = (value: string) => {
  return (
    ALLOWED_ONLY_NUMBERS.test(value) && value.length <= MAX_ZIP_CODE_LENGTH
  );
};

export const onSelectKeyDownAllowOnlyLetters: React.KeyboardEventHandler<
  HTMLDivElement
> = (event) => {
  const target = event.target as HTMLInputElement;
  const proposedValue = target.value + event.key;

  if (!ALLOWED_ONLY_ALPHABETICAL_CHARACTERS_REGEX.test(proposedValue)) {
    event.preventDefault();
  }
};

export const onSelectKeyDownAllowOnlyLettersAndSpaces: React.KeyboardEventHandler<
  HTMLDivElement
> = (event) => {
  const target = event.target as HTMLInputElement;
  const proposedValue = target.value + event.key;

  if (!ALLOWED_ONLY_ALPHABETICAL_CHARACTERS_AND_SPACES.test(proposedValue)) {
    event.preventDefault();
  }
};

/* Validation of NPI number based on
  https://www.cms.gov/Regulations-and-Guidance/Administrative-Simplification/NationalProvIdentStand/Downloads/NPIcheckdigit.pdf
  Allows for numbers without prefix
  Without prefix: "1234567893" => Valid
*/

function validateNPIWithoutPrefix(npi: string): boolean {
  if (npi.length === 10) {
    let doubleSum = 0;
    let singleSum = 0;

    // Ensure that NPI number starts with 1 or 2 to be valid
    if (['1', '2'].includes(npi[0])) {
      for (let i = 0; i < 9; i += 2) {
        const doubledDigit = parseInt(npi[i]) * 2;
        doubleSum += doubledDigit < 10 ? doubledDigit : (doubledDigit % 10) + 1;
      }

      for (let i = 1; i < 9; i += 2) {
        singleSum += parseInt(npi[i]);
      }

      if (
        10 - ((doubleSum + singleSum + 24) % 10) === parseInt(npi[9]) ||
        (doubleSum + singleSum + 24) % 10 === parseInt(npi[9])
      ) {
        return true;
      }
    }
  }

  return false;
}

/* Validation of NPI number based on
  https://www.cms.gov/Regulations-and-Guidance/Administrative-Simplification/NationalProvIdentStand/Downloads/NPIcheckdigit.pdf
  Allows for numbers with prefix
  With prefix: "808401234567893" => Valid
*/
function validateNPIWithPrefix(npi: string): boolean {
  if (npi.length === 15 && npi.startsWith('80840')) {
    const npiDigits: number[] = npi.slice(5).split('').map(Number);
    let doubleSum = 0;
    let singleSum = 0;

    for (let i = 0; i < 9; i += 2) {
      const doubledDigit = npiDigits[i] * 2;
      doubleSum += doubledDigit < 10 ? doubledDigit : (doubledDigit % 10) + 1;
    }

    for (let i = 1; i < 9; i += 2) {
      singleSum += npiDigits[i];
    }

    if (
      10 - ((doubleSum + singleSum) % 10) === npiDigits[9] ||
      (doubleSum + singleSum) % 10 === npiDigits[9]
    ) {
      return true;
    }
  }

  return false;
}

export function validateNPI(value: string): boolean {
  if (value.startsWith('80840')) {
    // If the value starts with the prefix "80840", use validateNPIWithPrefix
    return validateNPIWithPrefix(value);
  } else {
    // Otherwise, use validateNPIWithoutPrefix
    return validateNPIWithoutPrefix(value);
  }
}
export const getFirstNameFromDisplayName = (displayName?: string): string => {
  if (!displayName) return '';
  const splittedDisplayName = displayName.split(' ');
  if (splittedDisplayName.length === 1) return splittedDisplayName[0];
  return splittedDisplayName.slice(0, -1).join(' ');
};

export const getLastNameFromDisplayName = (displayName?: string): string => {
  if (!displayName) return '';
  const splittedDisplayName = displayName.split(' ');
  if (splittedDisplayName.length === 1) return '';
  return splittedDisplayName.splice(-1, 1).join(' ');
};

export const replacePlusSignFromDisplayedName = (
  displayName?: string | null,
): string => {
  if (!displayName) return '';
  return displayName?.replaceAll('+', ' ');
};

export const parsePhoneNumber = (
  input: string,
): { country: string; number: string; extension: string | null } | null => {
  const match = input.match(FULL_PHONE_NUMBER_SEPARATED_BY_GROUPS_REGEX);

  if (match) {
    const [, country, number, extension] = match;
    return {
      country,
      number,
      extension: extension || null,
    };
  }

  return null;
};

export const parseQuestionOptionsForDropdownWithLookup = (
  options: string[],
) => {
  const parsedOptions = options.map((option: string) => ({
    label: option,
    value: option,
  }));
  return parsedOptions;
};

export const parseMultipleValuesIntoSelectFormat = (values: string[]) => {
  return values.map((value: string) => {
    return { label: value, value: value };
  });
};

export const parseValueIntoSelectFormat = (value: string): DropdownItem => {
  return { label: value, value: value };
};

export const getSelectedValues = (
  parsedOptions: DropdownItem[],
  selectedOptions: string[],
): DropdownItem[] => {
  const filteredOptions = parsedOptions.filter((obj: DropdownItem) => {
    return selectedOptions.includes(obj.value);
  });

  const customValues = selectedOptions.filter((value) => {
    return !parsedOptions.some((obj) => obj.value === value);
  });

  const customOptions = customValues.map((option) => {
    return {
      label: option,
      value: option,
    };
  });

  return [...filteredOptions, ...customOptions];
};

interface QuestionItem {
  id: number;
  text: string;
  subtitle: string;
  answers: string[];
}

export const findQuestionById = (
  questions: GetUnauthenticatedQuestionsByIdsQuery,
  id: number,
): QuestionItem | undefined => {
  const foundQuestion = questions.unauthenticated_questions.find(
    (question) => question.id === id,
  );

  if (foundQuestion) {
    return {
      id: foundQuestion.id,
      text: foundQuestion.text,
      answers: foundQuestion.answers ?? [],
      subtitle: foundQuestion.subtitle ?? '',
    };
  }
};

export const convertHealthQuestionnaireResponsesFromFhirToHasura = (
  newData: GetFhirQuestionnaireResponseQuery,
):
  | GetQuestionnaireResponsesByUserAndQuestionnaireIdQuery
  | GetLatestQuestionnaireByIdQuery => {
  if (
    !newData.getFHIRQuestionnaireResponse.questionnaire_response.responses
      .length
  ) {
    return {
      questionnaire_responses: [],
    };
  }
  const questionnaireResponseId =
    newData.getFHIRQuestionnaireResponse.questionnaire_response.id;
  const newResponses =
    newData.getFHIRQuestionnaireResponse.questionnaire_response.responses;
  return {
    questionnaire_responses: [
      {
        ...(questionnaireResponseId && { id: questionnaireResponseId }),
        responses: newResponses.map((response) => {
          const castedResponse = response as HasuraAnswerItem;
          switch (castedResponse.responseType) {
            case 'string':
              return {
                questionID: castedResponse.questionID,
                response: castedResponse.stringResponse,
                currentSection: castedResponse.currentSection,
                currentIndexOfSection: castedResponse.currentIndexOfSection,
              };

            case 'string[]':
              if (
                castedResponse.questionID === SYSTOLIC_AND_DIASTOLIC_QUESTION_ID
              ) {
                return {
                  questionID: castedResponse.questionID,
                  currentSection: castedResponse.currentSection,
                  response: castedResponse.stringArrayResponse?.map(
                    (sugarResp: string, idx: number) => ({
                      questionID: castedResponse.questionID,
                      response: sugarResp,
                      inputId: `input-${idx + 1}`,
                      inputLabel: idx === 0 ? 'Systolic' : 'Diastolic',
                    }),
                  ),
                };
              }
              return {
                questionID: castedResponse.questionID,
                response: castedResponse.stringArrayResponse,
                currentSection: castedResponse.currentSection,
                currentIndexOfSection: castedResponse.currentIndexOfSection,
              };

            // question ID 99 response structure
            case 'HasuraItem[]':
              if (castedResponse.questionID === 850) {
                return {
                  currentSection: castedResponse.currentSection,
                  questionID: castedResponse.questionID,
                  response: castedResponse.hasuraItemArrayResponse,
                };
              }
              return {
                questionID: castedResponse.questionID,
                currentSection: castedResponse.currentSection,
                response: castedResponse.hasuraItemArrayResponse?.map(
                  (resp, idx) => {
                    switch (resp.responseType) {
                      case 'string[]':
                        return resp.stringArrayResponse;

                      case 'HasuraItem[]':
                        // each medication response structure
                        return {
                          id: idx,
                          title: `Medication list ${idx + 1}`,
                          completed: false,
                          answers: (resp?.hasuraItemArrayResponse ?? [])?.map(
                            (innerResp) => {
                              switch (innerResp.responseType) {
                                case 'HasuraItem[]':
                                case 'string[]':
                                  return {
                                    questionID: innerResp.questionID,
                                    response: innerResp.stringArrayResponse,
                                  };

                                case 'string':
                                  return {
                                    questionID: innerResp.questionID,
                                    response: innerResp.stringResponse,
                                  };

                                case 'HasuraMedicationItem':
                                  return {
                                    questionID: innerResp.questionID,
                                    response: {
                                      id: innerResp.hasuraMedicationItemResponse
                                        ?.id,
                                      value:
                                        innerResp.hasuraMedicationItemResponse
                                          ?.value,
                                    },
                                  };

                                default:
                                  throw new Error(
                                    `Unexpected response type from FHIR inside of HasuraItem[] - HasuraMedicationItem: ${innerResp.responseType}`,
                                  );
                              }
                            },
                          ),
                        };
                      default:
                        throw new Error(
                          `Unexpected response type from FHIR inside of HasuraItem[]: ${resp.responseType}`,
                        );
                    }
                  },
                ),
              };

            default:
              throw new Error(
                `Unexpected response type from FHIR: ${response.responseType}`,
              );
          }
        }),
      },
    ],
  };
};

export const convertDNAQuestionnaireResponsesFromFhirToHasura = (
  newData: GetFhirQuestionnaireResponseQuery,
):
  | GetQuestionnaireResponsesByUserAndQuestionnaireIdQuery
  | GetLatestQuestionnaireByIdQuery => {
  if (
    !newData.getFHIRQuestionnaireResponse.questionnaire_response.responses
      .length
  ) {
    return {
      questionnaire_responses: [],
    };
  }
  const questionnaireResponseId =
    newData.getFHIRQuestionnaireResponse.questionnaire_response.id;
  const newResponses =
    newData.getFHIRQuestionnaireResponse.questionnaire_response.responses;
  return {
    questionnaire_responses: [
      {
        ...(questionnaireResponseId && { id: questionnaireResponseId }),
        responses: newResponses.map((response) => {
          const castedResponse = response as HasuraAnswerItem;
          switch (castedResponse.responseType) {
            case 'string':
              return {
                questionID: castedResponse.questionID,
                response: castedResponse.stringResponse,
                currentSection: castedResponse.currentSection,
                currentIndexOfSection: castedResponse.currentIndexOfSection,
              };

            case 'string[]':
              return {
                questionID: castedResponse.questionID,
                response: castedResponse.stringArrayResponse,
                currentSection: castedResponse.currentSection,
                currentIndexOfSection: castedResponse.currentIndexOfSection,
              };

            case 'HasuraItem[]':
              return {
                questionID: castedResponse.questionID,
                currentSection: castedResponse.currentSection,
                currentIndexOfSection: castedResponse.currentIndexOfSection,
                response: castedResponse.hasuraItemArrayResponse?.map(
                  (resp) => {
                    switch (resp.responseType) {
                      case 'string':
                        return {
                          questionID: resp.questionID,
                          response: resp.stringResponse,
                        };

                      case 'HasuraItem[]':
                        return {
                          questionID: resp.questionID,
                          response: resp.hasuraItemArrayResponse?.map(
                            (data) => ({
                              questionID: data.questionID,
                              response: data.stringResponse,
                            }),
                          ),
                        };
                      default:
                        throw new Error(
                          `Unexpected response type from FHIR: ${response.responseType}`,
                        );
                    }
                  },
                ),
              };

            default:
              throw new Error(
                `Unexpected response type from FHIR: ${response.responseType}`,
              );
          }
        }),
      },
    ],
  };
};

// Clears all local storage keys except for the ones inside the parameters
// If it doesn't have anything passed to it, it clears everything
export const clearLocalStorageExceptKeys = (keysToKeep?: string[]) => {
  if (keysToKeep && keysToKeep.length > 0) {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    }
  } else {
    localStorage.clear();
  }
};

export const extractUUIDFromResURL = (url: string): string => {
  if (url.includes('/')) {
    const match = url.match(/\/([^/]+)\/?$/);
    return match ? match[1] : '';
  }
  return url;
};

export function fromFhirAppointmentTypeToLocal(
  appointmentType?: AppointmentTypeEnum | null,
): AppointmentTypeEnum {
  switch (appointmentType) {
    case AppointmentTypeEnum.Chat:
    case AppointmentTypeEnum.Chat_30:
    case AppointmentTypeEnum.Chat_60:
      return AppointmentTypeEnum.Chat;
    case AppointmentTypeEnum.Email:
      return AppointmentTypeEnum.Email;
    case AppointmentTypeEnum.Video:
    case AppointmentTypeEnum.Video_30:
    case AppointmentTypeEnum.Video_60:
      return AppointmentTypeEnum.Video;
    default: // Dnavisit as default
      return AppointmentTypeEnum.Dnavisit;
  }
}

export function patientBioRouteByPatientId(patientId: string): string {
  return `${MY_PATIENTS}/${patientId}/${MY_PATIENTS_BIO_INFO}`;
}

export const getBrowserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

type FormatDateForAppointmentOptions = {
  simplified?: boolean;
  withTimeZone?: boolean;
};

export const getZonedMoment = (date: Date | string) => {
  const tz = getBrowserTimeZone();
  const m = moment(date);
  if (!m.isValid()) return null;
  return m.tz(tz);
};

export const formatTimeZone = (m: moment.Moment) => {
  const abbr = m.format('z');
  return abbr || `GMT${m.format('Z')}`;
};

export const formatDateForAppointment = (
  date: Date | string,
  options: FormatDateForAppointmentOptions = {},
): string => {
  const { simplified = false, withTimeZone = true } = options;

  const m = getZonedMoment(date);
  if (!m) return '';

  const base = simplified
    ? m.format('MM/DD/YYYY hh:mm A')
    : m.format('dddd, MMMM D, YYYY hh:mm A');

  return withTimeZone ? `${base} ${formatTimeZone(m)}` : base;
};

export const formatDateTime = (date: Date | string, format: string): string => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'GMT';
  const m = moment(date);

  if (!m.isValid()) return '';

  return m.tz(tz).format(format);
};

export function asArray<T>(value: Promise<T | T[] | undefined>): Promise<T[]>;
export function asArray<T>(value: T | T[] | undefined): T[];
export function asArray<T>(
  value: (T | T[] | undefined) | Promise<T | T[] | undefined>,
): T[] | Promise<T[]> {
  const logic = (items: T | T[] | undefined): T[] => {
    if (!items) return [];
    return Array.isArray(items) ? items : [items];
  };
  if (value instanceof Promise) {
    return value.then(logic);
  }
  return logic(value);
}

export function singleOrDefault<T, D = null>(
  arr: Promise<T[]>,
  def: D,
): Promise<T | D>;
export function singleOrDefault<T, D = null>(arr: Promise<T[]>): Promise<T | D>;
export function singleOrDefault<T, D = null>(arr: T[], def?: D): T | D;
export function singleOrDefault<T, D = null>(arr: T[]): T | D;
export function singleOrDefault<T, D = null>(
  arr: T[] | Promise<T[]>,
  def: D = null as unknown as D,
): T | D | Promise<T | D> {
  if (!arr) return def;

  const logic = (items: T[]): T | D => {
    if (items.length === 0) return def;
    if (items.length !== 1)
      throw new Error(
        'More than one item found in array that expects zero or one entry',
      );
    return items[0];
  };

  return Array.isArray(arr) ? logic(arr) : arr.then(logic);
}

export function single<T>(arr: Promise<T[]>): Promise<T>;
export function single<T>(arr: T[]): T;
export function single<T>(arr: T[] | Promise<T[]>): T | Promise<T> {
  const logic = (items: T[]): T => {
    if (items.length !== 1)
      throw new Error(
        `${items.length} items found in array that expects exactly one entry`,
      );
    return items[0];
  };

  return Array.isArray(arr) ? logic(arr) : arr.then(logic);
}
export const parseQuestionOptionsForDropdownWithProperties = (
  options: DropdownItem[],
) => {
  const parsedOptions = options.map((option: DropdownItem) => ({
    label: option.label,
    value: option.value,
  }));
  return parsedOptions;
};

export const calculateAge = (birthDateString: string): number => {
  const birthDate = new Date(birthDateString);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

export const calculateUserAge = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);

  const birthDate = new Date(year, month - 1, day);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  // Adjust the age if the birth date has not occurred yet this year
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

export const formatTestKitNameForLocaleUsage = (
  testKitId: Hasura_Test_Types_Enum | undefined,
) => {
  switch (testKitId) {
    case Hasura_Test_Types_Enum.DnaSkin:
      return 'testDnaSkin';

    case Hasura_Test_Types_Enum.Skintelligent:
      return 'testSkintelligent';

    case Hasura_Test_Types_Enum.DnaVitamin:
      return 'testDnaVitamin';
    case Hasura_Test_Types_Enum.ZrtHeavyMetals:
    case Hasura_Test_Types_Enum.ZrtHormone:
    case Hasura_Test_Types_Enum.ZrtInflammatory:
    case Hasura_Test_Types_Enum.ZrtNeurotransmitters:
      return 'testHormone';
    default:
      return null;
  }
};

export function parseMediaToSharedMedia(
  medias: GetFhirPatientMediaQuery['getFHIRMediaByRequestPatientCodexID']['media'],
): SharedMedia[] {
  return medias
    .filter((media) => !!media.content.url)
    .map<SharedMedia>((media) => {
      const appointmentId =
        media.identifier?.find(
          ({ type }) => type?.text === MediaIdentifiersTypes.APPOINTMENT_ID,
        )?.value || undefined;
      const dermscoreId =
        media.identifier?.find(
          ({ type }) => type?.text === MediaIdentifiersTypes.DERMSCORE_ID,
        )?.value || undefined;
      const bodySite = media.bodySite?.coding?.[0].code || undefined;
      return {
        date: new Date(media.meta.lastUpdated),
        id: media.id,
        fileId: media.fileId,
        img: media.content.url as string,
        appointmentId,
        dermscoreId,
        bodySite,
        description: media.description || undefined,
      };
    });
}

export function getAge(dateOfBirth: Date): number {
  if (!dateOfBirth) {
    return 0;
  }
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

export interface DateLocale {
  monthNames: string;
}

export const formatDateWithLocales = (locales: DateLocale, date: number) => {
  const monthNames = Object.values(locales.monthNames);
  if (monthNames.length !== 12) {
    console.error('Locales object must have exactly 12 month names.');
    return '';
  }

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    console.error('Invalid date provided.');
    return '';
  }

  const month = monthNames[parsedDate.getMonth()];
  const day = String(parsedDate.getDate()).padStart(2, '0'); // Ensures day is two digits
  const year = parsedDate.getFullYear();

  const formattedDate = `${month} ${day}, ${year}`;

  return formattedDate;
};

export const formatDateToMonthAndDay = (dateTimeString: number): string => {
  const dateObj = new Date(dateTimeString);
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  const dateString = `${month}/${day}`;

  return dateString;
};

export const generateAppointmentLogs = (
  events: AppointmentEvent[],
  logs: Record<string, string>,
) => {
  const logsToReturn: string[] = [];

  const eventMap = new Map<Appointment_Event, AppointmentEvent>();

  events.forEach((event) => {
    eventMap.set(event.event, event);
  });

  if (eventMap.has(Appointment_Event.AppointmentEnd)) {
    logsToReturn.push(logs.appointmentComplete);
  } else {
    if (!eventMap.has(Appointment_Event.ProviderJoined)) {
      logsToReturn.push(logs.providerNotAttend);
    }

    if (!eventMap.has(Appointment_Event.PatientJoined)) {
      logsToReturn.push(logs.patientNotAttend);
    }

    const appointmentStartEvent = eventMap.get(
      Appointment_Event.AppointmentStart,
    );
    if (appointmentStartEvent) {
      const providerJoinedEvent = eventMap.get(
        Appointment_Event.ProviderJoined,
      );
      const patientJoinedEvent = eventMap.get(Appointment_Event.PatientJoined);

      if (providerJoinedEvent && appointmentStartEvent) {
        const providerLateMinutes = Math.round(
          (new Date(appointmentStartEvent.date).getTime() -
            new Date(providerJoinedEvent.date).getTime()) /
            (1000 * 60),
        );
        if (providerLateMinutes > 0) {
          logsToReturn.push(
            logs.providerLate.replace(
              '{{minutes}}',
              providerLateMinutes.toString(),
            ),
          );
        }
      }

      if (patientJoinedEvent && appointmentStartEvent) {
        const patientLateMinutes = Math.round(
          (new Date(appointmentStartEvent.date).getTime() -
            new Date(patientJoinedEvent.date).getTime()) /
            (1000 * 60),
        );
        if (patientLateMinutes > 0) {
          logsToReturn.push(
            logs.patientLate.replace(
              '{{minutes}}',
              patientLateMinutes.toString(),
            ),
          );
        }
      }
    }
  }

  return logsToReturn;
};

export function appointmentTypeFromServiceType(
  serviceType: ServiceType,
): AppointmentTypeEnum {
  switch (serviceType) {
    case ServiceType.Chat:
      return AppointmentTypeEnum.Chat;
    case ServiceType.Video:
      return AppointmentTypeEnum.Video;
    default:
      return AppointmentTypeEnum.Video;
  }
}

export type VoidCallback<TArg = undefined> = (arg: TArg) => void;
export type CloseModalCallback<TArg = undefined> = (
  arg: TArg,
) => Promise<unknown> | undefined;

export type Callback<
  TReturns = unknown,
  TArg1 = undefined,
  TArg2 = undefined,
  TArg3 = undefined,
  TArg4 = undefined,
  TArg5 = undefined,
> = TArg1 extends undefined
  ? () => TReturns
  : TArg2 extends undefined
  ? (arg1: TArg1) => TReturns
  : TArg3 extends undefined
  ? (arg1: TArg1, arg2: TArg2) => TReturns
  : TArg4 extends undefined
  ? (arg1: TArg1, arg2: TArg2, arg3: TArg3) => TReturns
  : TArg5 extends undefined
  ? (arg1: TArg1, arg2: TArg2, arg3: TArg3, arg4: TArg4) => TReturns
  : (
      arg1: TArg1,
      arg2: TArg2,
      arg3: TArg3,
      arg4: TArg4,
      arg5: TArg5,
    ) => TReturns;

export const mediaEncodeStringStartsWith = 'media';
export const encodeMediaInChat = (
  mediaId: string,
  mediaUrl: string,
  type: string = mediaEncodeStringStartsWith,
  fileName?: string,
  fileSize?: number | null,
) => {
  const date = moment().toISOString();
  return `${
    fileName ?? mediaId
  };${type};${fileSize};${mediaId};${date};${mediaUrl}`;
};
export const decodeMediaInChat = (
  message: string,
): {
  mediaId: string;
  mediaUrl: string;
  contentType: string;
  fileName: string;
  fileSize: string;
} | null => {
  if (!message) {
    return null;
  }
  const [fileName, type, fileSize, mediaId, , ...mediaUrl] =
    message?.split(';') ?? [];
  if (mediaId && mediaUrl) {
    return {
      mediaId,
      mediaUrl: mediaUrl.join(';'),
      contentType: type,
      fileName,
      fileSize,
    };
  }
  return null;
};

export const transformNavBarOptionsToLowercase = (
  navBarOption: string,
): string => {
  if (!navBarOption) {
    return navBarOption;
  }

  const navBarOptionElements = navBarOption.split(' ');
  const navBarTransformedList = navBarOptionElements.map(
    (navBarOptionElement) => {
      let navBarOptionElementTransformed = navBarOptionElement;
      if (!capitalizedLabels.includes(navBarOptionElement)) {
        navBarOptionElementTransformed = navBarOptionElement.toLowerCase();
      }

      return navBarOptionElementTransformed;
    },
  );
  const navBarTransformed = navBarTransformedList.join(' ');
  return (
    navBarTransformed.substring(0, 1).toUpperCase() +
    navBarTransformed.substring(1)
  );
};

export const getUserAge = (dob: Date) => {
  const ageDifMs = Date.now() - Number(dob);
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export const getDaysInMonth = (currentDate: Date) => {
  const days = [];
  const date = new Date(currentDate);
  while (date.getMonth() === currentDate.getMonth()) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

export type TimeString = `${number}:${number}` | `${string}:${string}`;

export function generateTimeSlots({
  interval = 15,
  startTime = '00:00',
  endTime = '23:59',
}: {
  interval?: number;
  startTime?: TimeString;
  endTime?: TimeString;
}): TimeString[] {
  const slots: TimeString[] = [];
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  const startMinutes = startHour * SECONDS_IN_MIN + startMinute;
  const endMinutes = endHour * SECONDS_IN_MIN + endMinute;

  for (let minutes = startMinutes; minutes <= endMinutes; minutes += interval) {
    const hours = Math.floor(minutes / MINUTES_IN_HOUR);
    const mins = minutes % SECONDS_IN_MIN;
    slots.push(
      `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`,
    );
  }

  return slots;
}

export const selectedDateFormat = (date: Date | string) => {
  const d = new Date(date);
  const weekDay = d.toLocaleDateString('en-US', { weekday: 'short' });
  const day = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${day} (${weekDay})`;
};

export const onlyNumbersFormat = (value: string) => {
  if (!value) return '';
  return value.replaceAll(/[^0-9]+/g, '');
};

export const formatPhoneNumber = (phone?: string) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

export const getAddress = (
  address: AddressInformationOutput | undefined | null,
) => {
  if (!address) {
    return undefined;
  }
  const { city, address1, state } = address;
  return [city, address1, state].filter(Boolean).join(', ');
};

export const capitalizeFirstWord = (str?: string) => {
  if (!str) return;

  const match = str.match(/^\s*(\p{L})(\p{L}*)/u);
  if (!match) return str;
  const [all, first, rest] = match;
  return str.replace(
    all,
    all.replace(first + rest, first.toUpperCase() + rest),
  );
};

export const formatMoney = (amount?: number, currency = 'USD') => {
  const value =
    typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

export const mapNotification = (
  notification: ApiNotification,
): Notification => ({
  id: notification.id,
  title: notification.data.title,
  message: notification.data.message,
  constants: notification.data.constants,
  date: relativeDate(notification.data.date),
  originalDate: notification.data.date,
  isRead: notification.status === User_Notification_Status_Enum.Read,
  action: notification.data.action,
  notificationEventType: notification.data.notificationEventType,
  extraActions: (notification.data.extraActions as AlertExtraActions[]) || [],
  activityType: notification.activity_type,
  type: notification.notification_type as number,
});
